// Tokens de diseño de Star Circle (STD-003), extraídos verbatim de
// context/prototype/DESIGN-TOKENS.md. No agregar valores que no estén en esa
// tabla: cualquier color/radio/sombra nuevo requiere actualizar el prototipo
// primero.
//
// Nota de finding conocido (DESIGN-TOKENS.md §3-4): el CSS del prototipo no
// declara token de error y cada superficie usa su propio hex — PLAN-004 §4.5
// prohíbe unificarlos por semejanza; solo una decisión humana con nuevas
// baselines podría convertirlos en un token global. `danger` de abajo es el
// del portal (#DFA394, admin.jsx:627): es lo único que el portal consume, y
// lo hace en JS (`tokens.danger.base`), nunca vía `var(--danger*)`.
// `validatorDanger` es el hex propio del validador (#E08975, validador.jsx:18)
// — ver `validatorCssVariables` más abajo para cómo se publica sin tocar el
// token global del portal.

export const tokens = {
  surface: {
    bg: "#0A0A0B",
    bg2: "#121214",
    bg3: "#17171A",
    surface: "rgba(255,255,255,0.045)",
    surface2: "rgba(255,255,255,0.072)",
    surface3: "rgba(255,255,255,0.105)",
    line: "rgba(255,255,255,0.085)",
    line2: "rgba(255,255,255,0.15)",
  },
  text: {
    primary: "#F3F3F1",
    secondary: "#A7A9AE",
    tertiary: "#6C6E73",
    disabled: "#4A4B50",
  },
  silver: {
    grad: "linear-gradient(135deg,#F6F7F8 0%,#C2C4C9 42%,#EFF0F1 60%,#9A9DA3 100%)",
    flat: "#CBCDD2",
  },
  // Champagne — solo recompensas y logros (star-circle.css:23). No usar como
  // acento genérico del portal.
  gold: {
    base: "#9FB0A6",
    light: "#C2D0C7",
    deep: "#6F8378",
    dim: "rgba(159,176,166,0.13)",
    line: "rgba(159,176,166,0.40)",
  },
  // Danger del portal (DESIGN-TOKENS.md §4): admin.jsx:627 (#DFA394). Los
  // literales `rgba` de abajo son la derivación en vivo del hex, no valores
  // independientes.
  danger: {
    base: "#DFA394",
    dim: "rgba(214,124,104,0.14)",
    line: "rgba(214,124,104,0.4)",
  },
  // Danger propio del validador (DESIGN-TOKENS.md §3-4): validador.jsx:18-20
  // (#E08975). No es intercambiable con `danger` de arriba — PLAN-004 §4.5.
  validatorDanger: {
    base: "#E08975",
    dim: "rgba(224,137,117,0.14)",
    line: "rgba(224,137,117,0.4)",
  },
  radius: {
    lg: "24px",
    sm: "15px",
  },
  shadow: "0 22px 48px -16px rgba(0,0,0,0.65), 0 3px 10px rgba(0,0,0,0.35)",
  font: {
    // Colisión resuelta en DESIGN-TOKENS.md §C-1: gana Hanken Grotesk en las
    // tres superficies pese a que :root declare Manrope/Cormorant Garamond.
    ui: "'Hanken Grotesk', system-ui, -apple-system, sans-serif",
    display: "'Hanken Grotesk', system-ui, -apple-system, sans-serif",
  },
  // C-3: gradiente de página de cada .dc.html, no --bg plano. Dos capas
  // (Star Circle Admin.dc.html:19-21): radial + linear.
  pageBackground:
    "radial-gradient(1200px 760px at 16% -10%, #1d1d22 0%, rgba(29,29,34,0) 55%), " +
    "linear-gradient(180deg, #0c0c0e 0%, #0a0a0b 100%)",
} as const;

export const cssVariables = `
  --bg: ${tokens.surface.bg};
  --bg2: ${tokens.surface.bg2};
  --bg3: ${tokens.surface.bg3};
  --surface: ${tokens.surface.surface};
  --surface2: ${tokens.surface.surface2};
  --surface3: ${tokens.surface.surface3};
  --line: ${tokens.surface.line};
  --line2: ${tokens.surface.line2};
  --text: ${tokens.text.primary};
  --text2: ${tokens.text.secondary};
  --text3: ${tokens.text.tertiary};
  --text4: ${tokens.text.disabled};
  --silver-grad: ${tokens.silver.grad};
  --silver: ${tokens.silver.flat};
  --gold: ${tokens.gold.base};
  --gold-2: ${tokens.gold.light};
  --gold-deep: ${tokens.gold.deep};
  --gold-dim: ${tokens.gold.dim};
  --gold-line: ${tokens.gold.line};
  --danger: ${tokens.danger.base};
  --danger-dim: ${tokens.danger.dim};
  --danger-line: ${tokens.danger.line};
  --radius: ${tokens.radius.lg};
  --radius-sm: ${tokens.radius.sm};
  --shadow: ${tokens.shadow};
  --ui: ${tokens.font.ui};
  --display: ${tokens.font.display};
`;

// Override de `--danger`/`--danger-dim`/`--danger-line` con el hex propio del
// validador (DESIGN-TOKENS.md §3-4, PLAN-004 §4.5). Se publica acotado a
// `.sc-shell` (contenedor raíz de sus tres pantallas de acceso,
// `validator/access/AccessLayout.tsx`) en vez de sobrescribir el token
// global: el portal no toca este bloque y sigue leyendo `tokens.danger.base`
// sin cambios.
export const validatorCssVariables = `
  --danger: ${tokens.validatorDanger.base};
  --danger-dim: ${tokens.validatorDanger.dim};
  --danger-line: ${tokens.validatorDanger.line};
`;
